// ============================================
// TRANSFER HOOKS
// Combines hooks from:
// - [Transfer.tsx] -> Transfer (TRANSFER 402-410)
// - [TransferHelpers.tsx] -> Transfer (TRANSFER 401, 411, 450-451, 413)
// - [TransferSourceForm.tsx] -> Transfer (TRANSFER 405, 414)
// - [useTransferAmounts.tsx] -> Transfer (TRANSFER 412-413)
// ============================================

import React, {useEffect, useMemo} from 'react';
import {FieldInputProps} from 'formik';
import {omit} from 'lodash-es';
import {
  mainPlanTypeType,
  recommendFieldsType,
  planAndSubplanAmounts,
} from '../CreateRecommend/recommendFields';
import {
  transferFieldsType,
  transferSourceType,
  transferExistingSubplanFields,
  transferNewSubplanFields,
} from '../RecommendsTransfer/transferFields';
import type {newPlanFields, newSubplanFields} from '../RecommendsSetUpNew/setUpNewFields';
import {Pot} from '../../../schema-types/Pot';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';
import {optionalRound} from '../CreateRecommend/RecommendSourceDestination/utils';
import {getPotInfo, PotInfo} from '../../../utils/getPotInfo';
import {defaultFeeFields, getPlanFeesFormikFromServer} from '../CreateRecommend/PlanFees';
import {nullableBooleanToYesNoEmpty} from '../RecommendsTransfer/nullableBooleanToYesNoEmpty';
import {getFeeFormikFromServer} from '../CreateRecommend/DAInitialFees';
import {POTS_BOND, POTS_PENSION} from '../../../utils/potsFilter';
import {UPDATES} from '../../../shared/UPDATES';
import {
  useAutoForm,
  useAutoField,
  AutoFieldHelpers,
  SetFieldValueWithSourceKey,
} from '../../../components/AutoForm/useAutoForm';
import usePlanNamesProviders from '../../../hooks/usePlanNamesProviders';
import {useSourcesAndDestinationPots} from '../../../hooks/useSourcesAndDestinationPots';
import {moneyFieldValueType} from '../../../components/AutoForm/AutoFormNew';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';

// ============================================
// Transfer.tsx hooks (402-410)
// ============================================

export const changeTransferDestination = ({
  value,
  event,
  sourcePots,
  transferDestAccountFieldHelpers,
  transferDestinationFieldHelpers,
}: {
  value: string;
  event: React.ChangeEvent<any> | null;
  sourcePots: Pot[];
  transferDestAccountFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationAccount']
  >;
  transferDestinationFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationValues']
  >;
}) => {
  const newVal = value || event?.target?.value;
  const pot = sourcePots.find((x) => x.id === newVal);
  transferDestAccountFieldHelpers.setValue(UPDATES._407, newVal, true);
  const childPots =
    pot?.children?.filter((x) =>
      x.productName?.acronym?.toLocaleLowerCase().includes('giagross')
    ) || [];
  const subplans = (childPots || []).reduce<
    Record<string, transferExistingSubplanFields>
  >((acc, x) => {
    const subplan: transferExistingSubplanFields = {
      checked: childPots.length === 1,
      existingPotId: x.id,
      singleClientAmount: '',
    };
    acc[x.id] = subplan;
    return acc;
  }, {});

  if (pot) {
    transferDestinationFieldHelpers.setValue(UPDATES._410, {
      subplans,
      singleClientAmount: '',
    });
  }
};

export const resetTransferSourcesOnPlanTypeChange = ({
  transferSourcesFieldHelpers,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
}) => {
  transferSourcesFieldHelpers.setValue(UPDATES._406, {}, true);
};

export const resetDestinationIfInSources = ({
  checkedListSources,
  transferDestAccountFieldObj,
  transferDestAccountFieldHelpers,
}: {
  checkedListSources: {total: string[]};
  transferDestAccountFieldObj: {value: transferFieldsType['destinationAccount']};
  transferDestAccountFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationAccount']
  >;
}) => {
  if (checkedListSources.total.includes(transferDestAccountFieldObj.value || '')) {
    transferDestAccountFieldHelpers.setValue(UPDATES._408, undefined);
  }
};

export const initTransferDefaults = ({
  setFieldValue,
}: {
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  setFieldValue(UPDATES._402, 'transfer.isPensionSharing', false);
  setFieldValue(UPDATES._403, 'transfer.isDependantsPension', false);
  setFieldValue(UPDATES._404, 'transfer.isAps', false);
};

export const addShellSourcePlan = ({
  checkedListSources,
  setFieldValue,
  shellProducts,
  mainPlanTypeValue,
}: {
  checkedListSources: {shellPlans: string[]};
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  shellProducts: {label: string; value: string}[];
  mainPlanTypeValue: mainPlanTypeType;
}) => {
  const numberOfShells = checkedListSources.shellPlans.length;
  const id = `shell-${numberOfShells + 1}`;

  let defaultPlanType: string | undefined;
  if (mainPlanTypeValue === 'pension' && shellProducts.length) {
    const giaGross = shellProducts.find((x) =>
      x.label.toLocaleLowerCase().includes('gia gross')
    );
    defaultPlanType = giaGross?.value;
  }

  const newSource: transferSourceType = {
    id,
    singleClientAmount: '',
    maxAmount: 0,
    checked: true,
    isShellPlan: true,
    planFees: {
      feeDeviation: false,
      DFMFeeId: null,
      productFeeRequired: null,
      DFMFeeRequired: null,
      FPFeeId: null,
      FPFeeRequired: null,
      productFeeId: null,
      planServerFee: null,
    },
    legacyInitialFee: null,
    initialFee: undefined,
    full: true,
    inSpecie: false,
    contactId: '',
    isAuthorityHeld: '',
    isDataGather: '',
    planType: defaultPlanType,
  };
  setFieldValue(UPDATES._409, `transfer.transferSources.${id}`, newSource);
};

export const useResetTransferSourcesOnPlanTypeChange = ({
  transferSourcesFieldHelpers,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
}) => {
  const {values} = useAutoForm<recommendFieldsType>();
  useEffect(() => {
    resetTransferSourcesOnPlanTypeChange({transferSourcesFieldHelpers});
  }, [values.mainPlanType]);
};

export const useResetDestinationIfInSources = ({
  checkedListSources,
  transferDestAccountFieldObj,
  transferDestAccountFieldHelpers,
}: {
  checkedListSources: {total: string[]};
  transferDestAccountFieldObj: {value: transferFieldsType['destinationAccount']};
  transferDestAccountFieldHelpers: AutoFieldHelpers<
    transferFieldsType['destinationAccount']
  >;
}) => {
  useEffect(() => {
    resetDestinationIfInSources({
      checkedListSources,
      transferDestAccountFieldObj,
      transferDestAccountFieldHelpers,
    });
  }, [transferDestAccountFieldObj.value, checkedListSources]);
};

export const useInitTransferDefaults = () => {
  const {setFieldValue} = useAutoForm<recommendFieldsType>();
  useEffect(() => {
    initTransferDefaults({setFieldValue});
  }, []);
};

// ============================================
// TransferHelpers.tsx hooks (401, 411, 450-451, 413)
// ============================================

export const getTotalTransferAmount = (sources: transferSourceType[]): number =>
  sources.reduce(
    (accumulator, currentValue) => accumulator + Number(currentValue?.singleClientAmount),
    0
  );

export const updateTransferSourceIdFactory = ({
  transferSourcesFieldObj,
  sourcePots,
  transferSourcesFieldHelpers,
  ovrecommendid,
}: {
  transferSourcesFieldHelpers: AutoFieldHelpers<
    transferFieldsType['transferSources'] | undefined
  >;
  sourcePots: Pot[];
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  ovrecommendid: string | null;
}): ((id: string) => void) => {
  return (id: string) => {
    if (transferSourcesFieldObj.value && transferSourcesFieldObj.value[id]?.checked) {
      transferSourcesFieldHelpers.setValue(
        UPDATES._401,
        omit(transferSourcesFieldObj.value, id)
      );
    } else {
      const source = sourcePots?.find((x) => x.id === id);

      const maxAmount = source ? source.totalValue : 0;

      const potInfo = source && ovrecommendid ? getPotInfo(source, ovrecommendid) : false;
      const isExistingShellPlanMadeInThisRecc =
        (source &&
          source.isShellPlan &&
          source.isDummy &&
          potInfo === PotInfo.existingShellPlanThisRec) ||
        false;
      const newSource: transferSourceType = {
        id,
        singleClientAmount: maxAmount || '',
        maxAmount,
        checked: true,
        isShellPlan: isExistingShellPlanMadeInThisRecc, // this is confusing im sorry, bad api lol
        planFees: {
          feeDeviation: false,
          DFMFeeId: null,
          productFeeRequired: null,
          DFMFeeRequired: null,
          FPFeeId: null,
          FPFeeRequired: null,
          planServerFee: null,
          productFeeId: null,
        },
        initialFee: undefined,
        legacyInitialFee: null,
        full: true,
        inSpecie: false,
        contactId: '',
        isAuthorityHeld: nullableBooleanToYesNoEmpty(source?.isAuthorityHeld),
        isDataGather: nullableBooleanToYesNoEmpty(source?.isDataGather),
      };
      transferSourcesFieldHelpers.setValue(UPDATES._401, {
        ...transferSourcesFieldObj.value,
        ...{[id]: newSource},
      });
    }
  };
};

export const setTransferCombinedAmounts = ({
  setFieldValue,
  transferSourcesFieldObj,
}: {
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
}) => {
  // it totals the amounts for calculateTransferFirstSubplanAmounts
  // Single AMOUNT
  if (transferSourcesFieldObj.value) {
    const sourcesArr = Object.entries(transferSourcesFieldObj.value);
    const combinedOneOffMovingAmount = sourcesArr.reduce((acc, curr) => {
      return acc + Number(curr[1]?.singleClientAmount || 0);
    }, 0);
    setFieldValue(
      UPDATES._450,
      'transfer.combinedOneOffMovingAmount',
      combinedOneOffMovingAmount || ''
    );
    setFieldValue(
      UPDATES._451,
      'transfer.combinedClientOneOffMovingAmount',
      combinedOneOffMovingAmount || ''
    );
  }
};

export const calculateTransferFirstSubplanAmounts = ({
  mainPlanType,
  values,
  setUpNewFirstSubplanClientAmountFieldHelpers,
  setUpNewFieldObj,
}: {
  mainPlanType: mainPlanTypeType;
  values: recommendFieldsType;
  setUpNewFirstSubplanClientAmountFieldHelpers: AutoFieldHelpers<
    newSubplanFields['singleClientAmount'] | undefined
  >;
  setUpNewFieldObj: FieldInputProps<newPlanFields | undefined>;
}) => {
  // subplans only exist for pensions and bonds
  if (mainPlanType !== POTS_PENSION && mainPlanType !== POTS_BOND) {
    return;
  }

  // updates first subplan to be the total amount contributed, minus the subplan2

  const singleClientAmount =
    (values.transfer?.combinedOneOffMovingAmount || 0) -
    (values.transfer?.newPlan?.subplan1?.checked
      ? Number(setUpNewFieldObj.value?.subplan1?.singleClientAmount || 0)
      : 0);
  setUpNewFirstSubplanClientAmountFieldHelpers.setValue(
    UPDATES._413,
    singleClientAmount,
    true
  );
};

export const loadSavedTransferPlans = ({
  recommendDetails,
  transferFieldHelpers,
  isSPPDA,
}: // addNewSourcePlans,
{
  recommendDetails: OVRecommendation | null;
  transferFieldHelpers: AutoFieldHelpers<transferFieldsType | undefined>;
  transferFieldObj: FieldInputProps<transferFieldsType | undefined>;
  isSPPDA: boolean;
  // addNewSourcePlans: (shells: shellInputs[]) => void;
}) => {
  if (!recommendDetails) {
    return;
  }
  const transfer: transferFieldsType = {
    transferSources: {},
    destinationValues: {} as any,
    isAps: recommendDetails.isAps || false,
    isDependantsPension: recommendDetails.isDependantsPension || false,
    isPensionSharing: recommendDetails.isPensionSharing || false,
  };

  let shellPlanIndex = 0;
  const tempShellPlans: transferFieldsType['transferSources'] = {};
  const savedDestinationPots =
    recommendDetails.pots?.filter((x) => x.planType === 'destination') || [];
  const parentPot = savedDestinationPots.find((x) => !x.pot.parent);
  const serverSubplans = savedDestinationPots
    .filter((x) => !!x.pot.parent)
    .sort((a, b) => {
      if (a.pot.isSaltus) {
        return -1;
      }
      if (b.pot.isSaltus) {
        return 1;
      }
      return 0;
    });

  if (parentPot) {
    const isNewPlan =
      parentPot.pot.isDummy && parentPot.pot.originalOvrId === recommendDetails.id;

    if (isNewPlan) {
      const model = parentPot?.pot?.modelPortfolio || parentPot?.pot?.mandate;
      const transferNewPlanFields: newPlanFields = {
        existingPotId: parentPot.pot.id,
        planProvider: parentPot.pot.productProvider?.id,
        inTrust: parentPot.pot?.inTrust || undefined,
        planType: parentPot.pot.productName?.id,
        model: model || undefined,
        // newSubplanList: [],
        planFees: getPlanFeesFormikFromServer(parentPot) || defaultFeeFields,
        contactId: parentPot.pot.contact?.id,
        parentGuardianId: recommendDetails?.parentGuardian?.id,
      };

      transfer.destinationAccount = NEW_PLAN_ID;

      serverSubplans.forEach((ovPot, i) => {
        const subplanPlanFees = getPlanFeesFormikFromServer(ovPot);
        const subplanModel = ovPot?.pot?.modelPortfolio || ovPot?.pot?.mandate;
        const subplanValues: transferNewSubplanFields = {
          planFees: subplanPlanFees,
          checked: true,
          existingPotId: ovPot.pot.id,
          parentId: ovPot.pot?.parent?.id,
          singleClientAmount: optionalRound(ovPot?.singleClientAmount) || '',
          planProvider: ovPot?.pot.productProvider?.id || undefined,
          productProviderId: ovPot?.pot.productProvider?.id || undefined,
          model: subplanModel || undefined,
        };
        if (i === 0) {
          transferNewPlanFields.subplan0 = subplanValues;
        } else {
          transferNewPlanFields.subplan1 = subplanValues;
        }
      });

      transfer.newPlan = transferNewPlanFields;
    } else {
      transfer.destinationAccount = parentPot.pot.id;

      const subplanValues: Record<string, transferExistingSubplanFields> = {};
      serverSubplans.forEach((subplan, i) => {
        const subplanPlanFees = getPlanFeesFormikFromServer(subplan);
        const subplanValue: transferExistingSubplanFields = {
          checked: true,
          existingPotId: subplan.pot.id,
          parentId: subplan.pot?.parent?.id,
          singleClientAmount: optionalRound(subplan?.singleClientAmount) || '',
          // planFees: subplanPlanFees,
          // planProvider: subplan?.pot.productProvider?.id || undefined,
          // productProviderId: subplan?.pot.productProvider?.id || undefined,
        };
        subplanValues[subplan.pot.id] = subplanValue;
      });

      const transferDestinationValues: transferFieldsType['destinationValues'] = {
        singleClientAmount: '',
        // planFees,
        subplans: subplanValues,
      };
      transfer.destinationValues = transferDestinationValues;
    }
  }

  const savedSourcePots =
    recommendDetails.pots?.filter((x) => x.planType === 'source') || [];
  const shellPlans = savedSourcePots.filter((ovPot) => {
    const potInfo = getPotInfo(ovPot.pot, recommendDetails.id);
    return potInfo === PotInfo.existingShellPlanThisRec;
  });
  const otherSources = savedSourcePots.filter(
    (ovPot) =>
      getPotInfo(ovPot.pot, recommendDetails.id) !== PotInfo.existingShellPlanThisRec
  );

  for (const ovPot of otherSources) {
    if (ovPot?.pot) {
      const newSource: transferSourceType = {
        id: ovPot?.pot?.id,
        singleClientAmount: optionalRound(ovPot?.singleClientAmount),
        full: ovPot.full || false,
        isAuthorityHeld: nullableBooleanToYesNoEmpty(ovPot.pot.isAuthorityHeld),
        isDataGather: nullableBooleanToYesNoEmpty(ovPot.pot.isDataGather),
        maxAmount: ovPot.pot.totalValue,
        contactId: ovPot.pot.contact?.id,
        checked: true,
        isShellPlan: false,
        planValue: ovPot.pot.currentDirectValue,
        inSpecie: ovPot?.inSpecie ?? ovPot?.pot?.inSpecie ?? undefined,
        initialFee: ovPot?.initialFee?.id,
        legacyInitialFee: getFeeFormikFromServer(isSPPDA, ovPot.legacyInitialFee),
        planFees: getPlanFeesFormikFromServer(ovPot) || defaultFeeFields,
      };
      tempShellPlans[ovPot?.pot?.id] = newSource as transferSourceType;
    }
  }

  type shellInputs = {
    id?: string | undefined;
    index?: number | undefined;
    brandNew?: boolean;
  };
  const shellInputsArr: shellInputs[] = [];

  for (const ovPot of shellPlans) {
    // debugger;
    if (ovPot?.pot) {
      const id = `shell-${shellPlanIndex}`; // newSource id needs to match shellPlan id
      const newSource: transferSourceType = {
        id,
        isShellPlan: true,
        checked: true,
        singleClientAmount: optionalRound(ovPot?.singleClientAmount),
        accountNumber: ovPot?.pot?.planNumber,
        planProvider: ovPot?.pot?.productProvider?.id,
        planType: ovPot?.pot?.productName?.id,
        full: ovPot.full || false,
        contactId: ovPot.pot.contact?.id || 'joint',
        inSpecie: ovPot?.inSpecie ?? ovPot?.pot?.inSpecie ?? undefined,
        maxAmount: ovPot.pot.totalValue,
        isAuthorityHeld: nullableBooleanToYesNoEmpty(ovPot.pot.isAuthorityHeld),
        isDataGather: nullableBooleanToYesNoEmpty(ovPot.pot.isDataGather),
        planValue: ovPot.pot.currentDirectValue,
        originalOvrId: ovPot?.pot.originalOvrId,
        originalShellPlanId: ovPot?.pot.id,
        initialFee: ovPot.initialFee?.id,
        legacyInitialFee: getFeeFormikFromServer(isSPPDA, ovPot.legacyInitialFee),
        planFees: getPlanFeesFormikFromServer(ovPot) || defaultFeeFields,
      };
      tempShellPlans[id] = newSource as transferSourceType;

      // we need to add this manually, otherwise the list won't be populated

      shellInputsArr.push({index: shellPlanIndex, id, brandNew: false});
      shellPlanIndex += 1;
    }
  }

  // addNewSourcePlans(shellInputs);
  transfer.transferSources = tempShellPlans;
  transferFieldHelpers.setValue(UPDATES._411, transfer, true);
};

// ============================================
// TransferSourceForm.tsx hooks (405, 414)
// ============================================

export const setTransferFullAmount = ({
  transferSourceField,
  maxAmount,
  transferSourceHelpers,
}: {
  transferSourceField: {value: transferSourceType | undefined};
  maxAmount: moneyFieldValueType;
  transferSourceHelpers: AutoFieldHelpers<transferSourceType | undefined>;
}) => {
  if (transferSourceField.value?.full && maxAmount) {
    transferSourceHelpers.setValue(UPDATES._414, {
      ...transferSourceField.value,
      singleClientAmount: String(maxAmount) || '',
    });
  }
};

export const deriveIsLisaTransferSource = ({
  lisaId,
  values,
  planTypeFieldValue,
  sourcesAndDestinations,
  setFieldValue,
}: {
  lisaId: string | undefined;
  values: recommendFieldsType;
  planTypeFieldValue: string | undefined;
  sourcesAndDestinations: {
    id: string;
    productName?: {formattedName?: string | null} | null;
  }[];
  setFieldValue: SetFieldValueWithSourceKey<recommendFieldsType>;
}) => {
  let isLisa = false;
  if (!lisaId || !values.transfer?.destinationAccount) {
    isLisa = false;
  } else if (values.transfer?.destinationAccount === 'newPlan') {
    isLisa = planTypeFieldValue === lisaId;
  } else {
    isLisa =
      sourcesAndDestinations.find((x) => x.id === values.transfer?.destinationAccount)
        ?.productName?.formattedName === 'L-ISA';
  }
  setFieldValue(UPDATES._405, 'isLisa', isLisa);
  return isLisa;
};

export const useSetTransferFullAmount = ({
  id,
  maxAmount,
}: {
  id: string | undefined;
  maxAmount: moneyFieldValueType;
}) => {
  const [transferSourceField, , transferSourceHelpers] = useAutoField<
    transferSourceType | undefined
  >(`transfer.transferSources[${id}]`);
  useEffect(() => {
    setTransferFullAmount({transferSourceField, maxAmount, transferSourceHelpers});
  }, [transferSourceField.value?.full, maxAmount]);
};

export const useDeriveIsLisaTransferSource = () => {
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const {sourcesAndDestinations} = useSourcesAndDestinationPots();
  const {lisaId, queryGetProducts} = usePlanNamesProviders();
  useEffect(() => {
    if (!lisaId) {
      queryGetProducts({variables: {}});
    }
  }, [lisaId]);
  const [planTypeField] = useAutoField<newPlanFields['planType']>(
    `transfer.newPlan.planType`
  );
  const isLisaDestination = useMemo(() => {
    return deriveIsLisaTransferSource({
      lisaId,
      values,
      planTypeFieldValue: planTypeField.value,
      sourcesAndDestinations,
      setFieldValue,
    });
  }, [
    lisaId,
    planTypeField.value,
    values.transfer?.destinationAccount,
    sourcesAndDestinations.length,
  ]);
  return isLisaDestination;
};

// ============================================
// useTransferAmounts.tsx hooks (412-413)
// ============================================

export const useTransferAmounts = () => {
  const {setFieldValue} = useAutoForm<recommendFieldsType>();
  const [combinedSingleAmountObj, combinedSingleAmountMeta, combinedSingleAmountHelpers] =
    useAutoField<transferFieldsType['combinedOneOffMovingAmount']>(
      'transfer.combinedOneOffMovingAmount'
    );
  const [destinationValuesObj, destinationValuesMeta, destinationValuesHelpers] =
    useAutoField<transferFieldsType['destinationValues']>('transfer.destinationValues');
  useEffect(() => {
    const subplansObj = destinationValuesObj.value?.subplans || {};
    const subplansEntries = Object.entries(subplansObj).filter(
      ([id, fields]) => fields.checked
    );
    // TODO: sort by subplan index
    const totalAmount = combinedSingleAmountObj.value || 0;
    if (Object.keys(subplansEntries).length > 1) {
      const totalToMinusFromFirstSubplan = subplansEntries
        .slice(1)
        .reduce((acc, [subplanKey, subplanValues]) => {
          return acc + Number(subplanValues.singleClientAmount || 0);
        }, 0);
      const firstSubplanEntry = subplansEntries[0];
      const firstSubplanKey = firstSubplanEntry[0];

      setFieldValue(
        UPDATES._412,
        `transfer.destinationValues.subplans.${firstSubplanKey}.singleClientAmount`,
        totalAmount - totalToMinusFromFirstSubplan,
        true
      );
    } else if (Object.keys(subplansEntries).length === 1) {
      const firstSubplanEntry = subplansEntries[0];
      const firstSubplanKey = firstSubplanEntry[0];

      setFieldValue(
        UPDATES._413,
        `transfer.destinationValues.subplans.${firstSubplanKey}.singleClientAmount`,
        totalAmount,
        true
      );
    }
  }, [combinedSingleAmountObj.value, destinationValuesObj.value?.subplans]);
};
